import { observer } from "mobx-react";
import { oneOf, object } from "prop-types";
import { SummaryColumns } from "@repo/utils";
import { useEstimateEditorSettings, useEstimateStructure, useEstimateTableSettings } from "@tools";
import {
  HoursCell,
  RateCell,
  TimeCell,
  TeamMembersCell,
  CompanyUnitCostCell,
  FixedCostCell,
  FixedCompanyCostCell,
  FixedCompanyProfitabilityCell,
  FixedCompanyProfitCell,
  MaterialsCostCell,
  MaterialsCompanyCostCell,
  MaterialsCompanyProfitCell,
  MaterialsCompanyProfitabilityCell,
  PriceModifierCostCell,
  PriceModifierCompanyCostCell,
  PriceModifierCompanyProfitCell,
  PriceModifierCompanyProfitabilityCell,
} from "./cells";

export const ColumnFactory = observer(({ type, data }) => {
  
  const { isSellerOrClient } = useEstimateEditorSettings();
  const { columns } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  const {
    hideAllPrices,
  } = structure;
  const {
    hasTeamMembersCol,
    hasTimeCol,
    currency,
    companyCurrency
  } = structure.settings;
  const {
    apply,
    hideSummaryCost,
    hideSummaryHours,
    hideSummaryRate,
    hideSummaryTeam,
    hideSummaryTime,
  } = structure.visibility;
  
  return <>
    {
      columns.map((column, index) => {
        
        const columnProps = {
          extendPadding: index === columns.length-1
        };
        
        switch (column.name) {
          case SummaryColumns.HOURS:
            return !(apply && hideSummaryHours) &&
              <HoursCell isWorkType {...columnProps} />;
          
          case SummaryColumns.TEAM:
            return !(apply && hideSummaryTeam) && hasTeamMembersCol &&
              <TeamMembersCell {...columnProps} />;
          
          case SummaryColumns.TIME:
            return !(apply && hideSummaryTime) && hasTimeCol &&
              <TimeCell {...columnProps} />;
        }
        
        if(!isSellerOrClient || hideAllPrices)
          return;
        
        switch(column.name) {
          case SummaryColumns.C_UNIT_COST_INNER:
            return <CompanyUnitCostCell useCompanyCurrency {...columnProps} />;
          
          case SummaryColumns.C_UNIT_COST:
            return currency !== companyCurrency &&
              <CompanyUnitCostCell {...columnProps} />;
          
          case SummaryColumns.RATE:
            return !(apply && hideSummaryRate) &&
              <RateCell {...columnProps} />;
          
          
          case SummaryColumns.COST:
            if(apply && hideSummaryCost)
              return;
            if(type === "price_modifier" && data)
              return <PriceModifierCostCell
                navigationKey={`sm-${column.name}`}
                priceModifier={data}
                {...columnProps}
              />;
            if(type === "materials")
              return <MaterialsCostCell
                navigationKey={`sm-${column.name}`}
                {...columnProps}
              />;
            if(type === "fixedCost")
              return <FixedCostCell
                navigationKey={`sm-${column.name}`}
                {...columnProps}
              />;
            break;
          
          case SummaryColumns.C_COST:
            if(type === "price_modifier")
              return <PriceModifierCompanyCostCell {...columnProps} />;
            if(type === "materials")
              return <MaterialsCompanyCostCell {...columnProps} />;
            if(type === "fixedCost")
              return <FixedCompanyCostCell {...columnProps} />;
            break;
            
          case SummaryColumns.C_PROFIT:
            if(type === "price_modifier")
              return <PriceModifierCompanyProfitCell  {...columnProps} />;
            if(type === "materials")
              return <MaterialsCompanyProfitCell  {...columnProps} />;
            if(type === "fixedCost")
              return <FixedCompanyProfitCell {...columnProps} />;
            break;
            
          case SummaryColumns.C_PROFITABILITY:
            if(type === "price_modifier")
              return <PriceModifierCompanyProfitabilityCell {...columnProps} />;
            if(type === "materials")
              return <MaterialsCompanyProfitabilityCell {...columnProps} />;
            if(type === "fixedCost")
              return <FixedCompanyProfitabilityCell {...columnProps} />;
        }
      })
    }
  </>
})

ColumnFactory.propTypes = {
  type: oneOf(["price_modifier", "materials", "fixedCost"]).isRequired,
  data: object,
}